import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  Upload, FileText, Loader2, CheckCircle2, LogOut,
  Building2, MessageSquare, CloudUpload, Eye, Inbox, X, Percent, Download, AlertTriangle,
  UserRound, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import useDraggableModal from '../hooks/useDraggableModal';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const FONT = "'Roboto', 'Segoe UI', system-ui, -apple-system, sans-serif";

const fmtFecha = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('es-AR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

const fmtPeso = (b) => {
  if (!b) return '';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
};

const DriveBadge = ({ status }) => {
  const map = {
    ok: { label: 'En Drive', cls: 'text-[#188038] bg-[#e6f4ea]' },
    pending: { label: 'Pendiente', cls: 'text-[#b06000] bg-[#feefc3]' },
    error: { label: 'Error Drive', cls: 'text-[#c5221f] bg-[#fce8e6]' },
  };
  const it = map[status] || map.pending;
  return (
    <span className={`px-2.5 py-1 rounded-full text-[11px] font-medium whitespace-nowrap ${it.cls}`}>
      {it.label}
    </span>
  );
};

// `embedded` = la página se renderiza dentro del panel admin (AdminLayout ya
// aporta su propio header y logout). Los usuarios "loader" la ven standalone,
// con el header propio.
const LoadDocsPage = ({ embedded = false }) => {
  const { token, user, logout, isAdmin, canLoadFima, canLoadCheques } = useAuth();
  const fileInputRef = useRef(null);

  const [cliente, setCliente] = useState('');
  const [comentario, setComentario] = useState('');
  const [tipoRetencion, setTipoRetencion] = useState('');
  const [tiposRetencion, setTiposRetencion] = useState([]);
  // Clase de documento DETECTADA automáticamente al elegir el archivo
  // (retencion | orden_pago | cheques_recibidos | movimientos_cc | sin_clasificar).
  // El usuario no la elige, salvo cuando es 'sin_clasificar' (no se pudo leer):
  // ahí elige la clase a mano con `tipoManual`.
  const [deteccion, setDeteccion] = useState({ tipo: null, label: '', cargando: false });
  const [tipoManual, setTipoManual] = useState('');  // clase elegida por el loader si es ilegible
  const [file, setFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [subiendo, setSubiendo] = useState(false);
  const [verificando, setVerificando] = useState(false);
  const [destacadoId, setDestacadoId] = useState(null);
  // Reintento de la copia a Drive (por documento y masivo).
  const [reintentandoId, setReintentandoId] = useState(null);
  const [reintentandoTodos, setReintentandoTodos] = useState(false);

  // Alerta no bloqueante cuando el tipo elegido no coincide con el detectado.
  const [alerta, setAlerta] = useState({
    open: false, detectadoId: null, detectadoNombre: '',
    seleccionadoNombre: '', evidencia: '',
  });
  const [cargas, setCargas] = useState([]);
  const [cargandoLista, setCargandoLista] = useState(false);

  // Visor de PDF en modal (sirve el archivo del filesystem propio del usuario).
  const [visor, setVisor] = useState({ open: false, url: '', nombre: '', cargando: false });
  const dragVisor = useDraggableModal(visor.open);

  const abrirVisor = useCallback(async (doc) => {
    setVisor({ open: true, url: '', nombre: doc.archivo_nombre || 'Documento', cargando: true });
    try {
      const res = await fetch(`${API_URL}/api/load-docs/file/${doc.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.detail || `Error ${res.status}`);
      }
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      setVisor((v) => ({ ...v, url, cargando: false }));
    } catch (err) {
      toast.error('No se pudo abrir el PDF: ' + err.message);
      setVisor({ open: false, url: '', nombre: '', cargando: false });
    }
  }, [token]);

  const cerrarVisor = useCallback(() => {
    setVisor((v) => {
      if (v.url) URL.revokeObjectURL(v.url);
      return { open: false, url: '', nombre: '', cargando: false };
    });
  }, []);

  // Cerrar con la tecla Escape.
  useEffect(() => {
    if (!visor.open) return;
    const onKey = (e) => { if (e.key === 'Escape') cerrarVisor(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [visor.open, cerrarVisor]);

  // Liberar el object URL al desmontar.
  useEffect(() => () => { if (visor.url) URL.revokeObjectURL(visor.url); }, [visor.url]);

  // El extracto del banco es el único que no es PDF: viene en Excel y tiene su
  // propio carril (no pasa por el detector de tipo ni por OCR).
  const esArchivoExcel = (f) => !!f && f.name.toLowerCase().endsWith('.xlsx');
  // Las imágenes (JPG/PNG) son únicamente capturas de Saldo FIMA. El backend
  // confirma con OCR que sea la pantalla "Inversiones en pesos".
  const esArchivoImagen = (f) => !!f && /\.(png|jpe?g)$/i.test(f.name);
  const esArchivoCsv = (f) => !!f && f.name.toLowerCase().endsWith('.csv');

  const validarArchivo = (f) => {
    if (!f) return true;
    const esPdf = f.type === 'application/pdf' || f.name.toLowerCase().endsWith('.pdf');
    if (esArchivoImagen(f)) {
      if (!canLoadFima) {
        toast.error('No tenés permiso para cargar Saldo FIMA (capturas JPG/PNG).');
        return false;
      }
      return true;
    }
    // El .csv solo existe para la tabla de cheques físicos en custodia. Quién
    // puede cargarlo lo decide el backend igual; acá se evita el viaje.
    const esCsv = f.name.toLowerCase().endsWith('.csv');
    if (esCsv) {
      if (!canLoadCheques) {
        toast.error('No tenés permiso para cargar los listados de cheques del banco.');
        return false;
      }
      return true;
    }
    if (!esPdf && !esArchivoExcel(f)) {
      toast.error(canLoadFima
        ? 'El archivo debe ser un PDF, el Excel (.xlsx) del extracto bancario o una captura (JPG/PNG) de Saldo FIMA.'
        : 'El archivo debe ser un PDF, o el Excel (.xlsx) del extracto bancario.');
      return false;
    }
    return true;
  };

  // Detección automática de la clase de documento al elegir el archivo.
  // Adapta el formulario: si es una orden de pago, el tipo de retención no
  // aplica (los tipos salen del propio PDF y los confirma el admin).
  const detectarClase = useCallback(async (f, respaldo = null) => {
    setDeteccion({ tipo: null, label: '', cargando: true });
    try {
      const fd = new FormData();
      fd.append('archivo', f);
      const res = await fetch(`${API_URL}/api/load-docs/detectar-documento`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (res.ok) {
        const d = await res.json();
        console.info('[load-docs] detectar-documento', { archivo: f.name, mime: f.type, bytes: f.size, respuesta: d });
        setDeteccion({
          tipo: d.tipo_documento || 'sin_clasificar',
          label: d.tipo_documento_label || 'Sin clasificar',
          cargando: false,
        });
        return;
      }
      console.warn('[load-docs] detectar-documento respondió', res.status, { archivo: f.name });
    } catch (err) {
      /* best-effort: el backend vuelve a detectar (con OCR) al subir */
      console.warn('[load-docs] detectar-documento falló', { archivo: f.name, error: String(err) });
    }
    // Para un archivo del banco NO se cae en 'sin_clasificar': eso obliga al
    // formulario a pedir una clase a mano y las clases del banco no están en el
    // select, así que el archivo quedaría imposible de subir. El backend vuelve
    // a detectar al subir, que es la detección que manda.
    setDeteccion(respaldo || { tipo: 'sin_clasificar', label: 'Sin clasificar', cargando: false });
  }, [token]);

  const elegirArchivo = (f) => {
    setFile(f || null);
    setTipoManual('');
    if (!f) {
      setDeteccion({ tipo: null, label: '', cargando: false });
    } else if (esArchivoImagen(f)) {
      // Captura de Saldo FIMA: el saldo lo lee el backend al subir (OCR).
      setDeteccion({ tipo: 'saldo_fima', label: 'Saldo FIMA', cargando: false });
    } else if (esArchivoCsv(f) || esArchivoExcel(f)) {
      // Los dos formatos del banco se los pregunta al backend, que lee el
      // ENCABEZADO: desde acá no hay forma de distinguir el extracto del
      // listado de eCheqs, porque los dos son .xlsx. Si la consulta falla, se
      // usa el respaldo por extensión, que nunca bloquea la carga.
      detectarClase(f, esArchivoCsv(f)
        ? { tipo: 'cheques_banco', label: 'Listado de cheques del banco', cargando: false }
        : { tipo: 'extracto_banco_excel', label: 'Movimientos del banco (Excel)', cargando: false });
    } else {
      detectarClase(f);
    }
  };

  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    if (f && !validarArchivo(f)) return;
    elegirArchivo(f || null);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files?.[0];
    if (f && validarArchivo(f)) elegirArchivo(f);
  };

  const cargarLista = useCallback(async () => {
    setCargandoLista(true);
    try {
      const res = await fetch(`${API_URL}/api/load-docs/mis-cargas`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setCargas(await res.json());
    } catch {
      /* silencioso */
    } finally {
      setCargandoLista(false);
    }
  }, [token]);

  useEffect(() => { cargarLista(); }, [cargarLista]);

  const cargarTipos = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/load-docs/tipos-retencion`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setTiposRetencion(await res.json());
    } catch {
      /* silencioso */
    }
  }, [token]);

  useEffect(() => { cargarTipos(); }, [cargarTipos]);

  // Cantidad de cargas cuya copia a Drive quedó pendiente o con error.
  const pendientesDrive = cargas.filter((c) => c.drive_status !== 'ok').length;

  // Reintenta la copia a Drive de UNA carga que quedó pendiente o con error.
  const reintentarDrive = async (c) => {
    setReintentandoId(c.id);
    try {
      const res = await fetch(`${API_URL}/api/load-docs/reintentar-drive/${c.id}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(typeof data.detail === 'string' ? data.detail : `Error ${res.status}`);
      }
      toast.success('Documento copiado a Drive.');
      cargarLista();
    } catch (err) {
      toast.error('No se pudo copiar a Drive: ' + err.message);
    } finally {
      setReintentandoId(null);
    }
  };

  // Reintenta TODAS las copias pendientes o con error (solo admin).
  const reintentarTodosDrive = async () => {
    setReintentandoTodos(true);
    try {
      const res = await fetch(`${API_URL}/api/load-docs/reintentar-drive`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(typeof data.detail === 'string' ? data.detail : `Error ${res.status}`);
      }
      if (data.con_error) toast.warning(data.mensaje);
      else toast.success(data.mensaje || 'Documentos copiados a Drive.');
      cargarLista();
    } catch (err) {
      toast.error('No se pudo reintentar: ' + err.message);
    } finally {
      setReintentandoTodos(false);
    }
  };

  const resetForm = () => {
    setCliente('');
    setComentario('');
    setTipoRetencion('');
    setTipoManual('');
    setFile(null);
    setDeteccion({ tipo: null, label: '', cargando: false });
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // SHA-256 del archivo, calculado en el navegador (mismo algoritmo que el backend).
  const calcularHash = async (f) => {
    const buf = await f.arrayBuffer();
    const digest = await crypto.subtle.digest('SHA-256', buf);
    return Array.from(new Uint8Array(digest))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
  };

  // Avisa que el archivo ya existe y, si es una carga propia, la resalta en el historial.
  const destacarDuplicado = (dup) => {
    if (dup.es_propio && dup.doc_id) {
      setDestacadoId(dup.doc_id);
      setTimeout(() => {
        const el = document.getElementById(`carga-${dup.doc_id}`);
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 80);
      setTimeout(() => setDestacadoId(null), 6000);
      toast.error('Este archivo ya lo subiste antes. Lo resaltamos en tu historial.');
    } else {
      const quien = dup.user_name ? ` por ${dup.user_name}` : '';
      toast.error(`Este archivo ya fue subido anteriormente${quien}. No se puede cargar de nuevo.`);
    }
  };

  // Sube el documento al backend. La clase (retención / orden de pago) la
  // detecta el backend automáticamente; solo se envía el tipo de retención
  // cuando aplica (documento detectado como retención suelta).
  const ejecutarUpload = async (tipoId) => {
    setSubiendo(true);
    try {
      const esSinClasificar = deteccion.tipo === 'sin_clasificar';
      const esExcelBanco = deteccion.tipo === 'extracto_banco_excel';
      const esSaldoFima = deteccion.tipo === 'saldo_fima';
      const claseEfectiva = esSinClasificar ? tipoManual : deteccion.tipo;
      const fd = new FormData();
      // El backend exige el campo: en Saldo FIMA va el nombre fijo de la cuenta.
      fd.append('cliente_nombre', esSaldoFima ? 'Saldo FIMA' : cliente.trim());
      fd.append('comentario', comentario.trim());
      // El tipo de retención SOLO aplica cuando la clase efectiva es retención.
      if (claseEfectiva === 'retencion' && tipoId) fd.append('tipo_retencion_id', tipoId);
      // Documento ilegible: el loader eligió la clase a mano.
      if (esSinClasificar && tipoManual) fd.append('tipo_documento_manual', tipoManual);
      // Para el .xlsx se manda 'extracto_banco_excel' como clase por defecto,
      // pero el backend la ignora si el encabezado dice que es el listado de
      // eCheqs: ese carril corre antes. Para el .csv no se manda nada — el
      // único que lo puede clasificar es el detector del backend.
      if (esExcelBanco) fd.append('tipo_documento_manual', 'extracto_banco_excel');
      fd.append('archivo', file);
      console.info('[load-docs] upload → enviando', {
        archivo: file.name, mime: file.type, bytes: file.size,
        deteccion_front: deteccion.tipo, tipoManual, claseEfectiva,
        tipo_retencion_id: claseEfectiva === 'retencion' ? tipoId : null,
      });

      const res = await fetch(`${API_URL}/api/load-docs/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const data = await res.json().catch(() => ({}));
      console.info('[load-docs] upload ← respuesta', res.status, {
        tipo_documento: data.tipo_documento, origen_texto: data.origen_texto,
        requiere_revision: data.requiere_revision, tipo_retencion: data.tipo_retencion_nombre,
        drive_status: data.drive_status, detail: data.detail,
      });
      // 409 = duplicado detectado en el backend (red de seguridad ante carreras).
      if (res.status === 409) {
        await cargarLista();
        destacarDuplicado(data.detail || {});
        return;
      }
      if (!res.ok) {
        const msg = typeof data.detail === 'string' ? data.detail : `Error ${res.status}`;
        throw new Error(msg);
      }

      const resumen = data.banco_resumen;
      const fima = data.fima_saldo;
      if (fima) {
        const saldoTxt = Number(fima.saldo).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        toast.success(`Saldo FIMA cargado: $${saldoTxt}. Ya se ve en el Panel Diario.`, { duration: 8000 });
        if (fima.diario_pct == null) {
          toast.info('No se pudieron leer las variaciones diaria/acumulada; se guardó solo el saldo.', { duration: 8000 });
        }
      } else if (resumen) {
        // Lo que importa del extracto no es el archivo: es cuántos movimientos
        // entraron al histórico y cuántos ya estaban de una carga anterior.
        const rango = resumen.desde === resumen.hasta
          ? resumen.desde
          : `${resumen.desde} a ${resumen.hasta}`;
        toast.success(
          `Extracto cargado (${rango}): ${resumen.nuevos} movimiento(s) nuevo(s), ` +
          `${resumen.ya_estaban} ya estaban.`,
          { duration: 8000 }
        );
        if (resumen.checksum?.presente && resumen.checksum.ok === false) {
          toast.warning('El total del pie del archivo no coincide con lo leído. Revisalo.', { duration: 10000 });
        }
        if (resumen.repetidos_en_archivo > 0) {
          toast.info(
            `${resumen.repetidos_en_archivo} movimiento(s) del archivo son idénticos entre sí ` +
            `(misma fecha, comprobante e importe). Se guardaron todos.`,
            { duration: 8000 }
          );
        }
        // Un código de concepto sin antecedentes cuenta como operativo, o sea
        // como cobro o como pago. Avisarlo acá es el único momento en que hay
        // alguien mirando: después se vuelve un número más del total.
        if (resumen.conceptos_nuevos?.length) {
          toast.warning(
            `Concepto bancario nunca visto: ${resumen.conceptos_nuevos.join(', ')}. ` +
            `Por ahora suma a Cobros o Pagos; avisá si tiene que ir a Tesorería.`,
            { duration: 15000 }
          );
        }
        if (resumen.error) toast.error('No se pudieron importar los movimientos: ' + resumen.error);
      } else if (data.drive_status === 'ok') {
        toast.success('Documento cargado y copiado a Drive.');
      } else {
        toast.success('Documento cargado. La copia a Drive quedó pendiente.');
      }
      resetForm();
      cargarLista();
    } catch (err) {
      toast.error('Error al cargar: ' + err.message);
    } finally {
      setSubiendo(false);
    }
  };

  // Pre-chequeos antes de subir:
  //   1) Duplicado por hash (BLOQUEA: cada archivo se sube una sola vez).
  //   2) Tipo de retención según el texto del PDF (alerta NO bloqueante; solo
  //      cuando el documento fue detectado como retención suelta).
  // Clase efectiva: si el documento no se pudo clasificar, la elige el loader.
  const esSinClasificar = deteccion.tipo === 'sin_clasificar';
  const claseEfectiva = esSinClasificar ? tipoManual : deteccion.tipo;
  const esRetencion = claseEfectiva === 'retencion';
  const esOrdenPago = claseEfectiva === 'orden_pago';
  // El extracto del banco es de una cuenta, no de un cliente.
  const esExcelBanco = deteccion.tipo === 'extracto_banco_excel';
  // 'cheques_banco' es el respaldo del front cuando no se pudo consultar; las
  // otras dos son las clases reales que devuelve el backend.
  const esChequesBanco = ['cheques_banco', 'cheques_echeq', 'cheques_custodia']
    .includes(deteccion.tipo);
  // Ningún archivo del banco es de un cliente: no se pide el nombre.
  const esArchivoDelBanco = esExcelBanco || esChequesBanco;
  // Saldo FIMA: es de la cuenta de inversión, no de un cliente.
  const esSaldoFima = deteccion.tipo === 'saldo_fima';
  const subir = async () => {
    if (!esArchivoDelBanco && !esSaldoFima && !cliente.trim()) {
      toast.error('Ingresá el nombre o razón social del cliente.');
      return;
    }
    if (!file) { toast.error('Seleccioná un archivo.'); return; }
    if (esSinClasificar && !tipoManual) {
      toast.error('No se pudo leer el documento. Elegí la clase de documento.');
      return;
    }
    // El tipo de retención solo es obligatorio para retenciones sueltas.
    if (esRetencion && !tipoRetencion) { toast.error('Seleccioná el tipo de retención.'); return; }

    setVerificando(true);

    // 1) Duplicado (best-effort en el front; el backend vuelve a chequear al subir).
    try {
      const hash = await calcularHash(file);
      const fdHash = new FormData();
      fdHash.append('archivo_hash', hash);
      const resDup = await fetch(`${API_URL}/api/load-docs/verificar-duplicado`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fdHash,
      });
      if (resDup.ok) {
        const dup = await resDup.json();
        if (dup.duplicado) {
          setVerificando(false);
          await cargarLista();
          destacarDuplicado(dup);
          return; // bloquea la carga
        }
      }
    } catch {
      /* si no se puede calcular/chequear, el backend bloqueará igual en /upload */
    }

    // 2) Tipo de retención (alerta no bloqueante). Solo aplica a retenciones
    //    sueltas; OP, cheques, movimientos y sin_clasificar suben directo.
    if (!esRetencion) {
      setVerificando(false);
      ejecutarUpload(null);
      return;
    }
    try {
      const fd = new FormData();
      fd.append('tipo_retencion_id', tipoRetencion);
      fd.append('archivo', file);
      const res = await fetch(`${API_URL}/api/load-docs/detectar-tipo`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (res.ok) {
        const d = await res.json();
        console.info('[load-docs] detectar-tipo', { archivo: file.name, tipo_elegido: tipoRetencion, respuesta: d });
        const dispara =
          d.tipo_detectado_id != null &&
          !d.coincide &&
          (d.confianza === 'alta' || d.confianza === 'media');
        if (dispara) {
          setAlerta({
            open: true,
            detectadoId: d.tipo_detectado_id,
            detectadoNombre: d.tipo_detectado_nombre || '',
            seleccionadoNombre: d.tipo_seleccionado_nombre || '',
            evidencia: d.evidencia || '',
          });
          return; // espera la decisión del usuario en el modal
        }
      }
    } catch {
      /* la verificación es best-effort: si falla, se sube igual */
    } finally {
      setVerificando(false);
    }

    ejecutarUpload(tipoRetencion);
  };

  const cerrarAlerta = () => setAlerta((a) => ({ ...a, open: false }));

  // "Cambiar a X": corrige el select y sube con el tipo detectado.
  const confirmarCambio = () => {
    const nuevo = String(alerta.detectadoId);
    setTipoRetencion(nuevo);
    cerrarAlerta();
    ejecutarUpload(nuevo);
  };

  // "Mantener y subir igual": respeta lo que eligió el usuario.
  const mantenerYSubir = () => {
    cerrarAlerta();
    ejecutarUpload(tipoRetencion);
  };

  const inputCls =
    'w-full bg-white border border-[#dadce0] rounded-lg px-4 py-3 text-[15px] text-[#202124] ' +
    'placeholder-[#80868b] focus:outline-none focus:border-[#1a73e8] focus:ring-1 focus:ring-[#1a73e8] transition-colors';

  return (
    <div
      className={`${embedded ? 'rounded-2xl overflow-hidden' : 'min-h-screen'} bg-white flex flex-col`}
      style={{ fontFamily: FONT }}
    >
      {/* App bar estilo Gmail — solo en modo standalone: dentro del panel admin
          el header y el logout los pone AdminLayout. */}
      {!embedded && (
        <header className="sticky top-0 z-20 bg-white border-b border-[#e0e0e0] px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 rounded-full bg-[#e8f0fe] flex items-center justify-center shrink-0">
              <CloudUpload className="w-5 h-5 text-[#1a73e8]" />
            </div>
            <div className="min-w-0">
              <h1 className="text-[20px] leading-tight text-[#202124] font-normal truncate">Carga de Documentos</h1>
              <p className="text-[12px] text-[#5f6368] truncate">Subí un PDF asociado a un cliente</p>
            </div>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <span className="hidden sm:block text-[13px] text-[#5f6368]">{user?.name || user?.email}</span>
            <div className="w-9 h-9 rounded-full bg-[#1a73e8] text-white flex items-center justify-center text-sm font-medium">
              {(user?.name || user?.email || '?')[0]?.toUpperCase()}
            </div>
            <button
              onClick={logout}
              title="Salir"
              className="w-9 h-9 rounded-full flex items-center justify-center text-[#5f6368] hover:bg-[#f1f3f4] transition-colors"
            >
              <LogOut className="w-5 h-5" />
            </button>
          </div>
        </header>
      )}

      {/* Cuerpo a todo el ancho */}
      <main className={`flex-1 w-full py-6 ${embedded ? 'px-4 sm:px-6' : 'px-4 sm:px-6 lg:px-10'}`}>
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)] gap-6 h-full">

          {/* Columna formulario */}
          <section className="bg-white rounded-2xl border border-[#e0e0e0] shadow-[0_1px_3px_rgba(60,64,67,0.15)] p-6 sm:p-8">
            <h2 className="text-[18px] text-[#202124] font-medium mb-6">Nuevo documento</h2>

            <div className="space-y-6">
              {/* Cliente: no aplica ni a Saldo FIMA ni a los listados del
                  banco, que no son documentos de un cliente. */}
              {!esSaldoFima && !esArchivoDelBanco && (
              <div>
                <label className="flex items-center gap-2 text-[13px] font-medium text-[#5f6368] mb-2">
                  <Building2 className="w-4 h-4 text-[#1a73e8]" /> Nombre o razón social del cliente
                </label>
                <input
                  type="text"
                  value={cliente}
                  onChange={(e) => setCliente(e.target.value)}
                  placeholder="Ej: Distribuidora del Sur S.A."
                  className={inputCls}
                />
              </div>
              )}

              {/* PDF: drag & drop */}
              <div>
                <label className="flex items-center gap-2 text-[13px] font-medium text-[#5f6368] mb-2">
                  <FileText className="w-4 h-4 text-[#1a73e8]" /> Archivo PDF
                </label>

                {file ? (
                  <div className="flex items-center gap-3 px-4 py-3 rounded-lg bg-[#e8f0fe] border border-[#d2e3fc]">
                    <FileText className="w-6 h-6 text-[#1a73e8] shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="text-[14px] text-[#202124] font-medium truncate">{file.name}</p>
                      <p className="text-[12px] text-[#5f6368] flex items-center gap-2 flex-wrap">
                        {fmtPeso(file.size)}
                        {deteccion.cargando ? (
                          <span className="inline-flex items-center gap-1 text-[#5f6368]">
                            <Loader2 className="w-3 h-3 animate-spin" /> Detectando clase…
                          </span>
                        ) : deteccion.tipo ? (
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${
                            esOrdenPago ? 'text-[#188038] bg-[#e6f4ea]' : 'text-[#1a73e8] bg-white'
                          }`}>
                            Detectado: {deteccion.label}
                          </span>
                        ) : null}
                      </p>
                      {esOrdenPago && (
                        <p className="text-[11px] text-[#5f6368] mt-0.5">
                          Las retenciones incluidas se leen del propio PDF; no hace falta elegir un tipo.
                        </p>
                      )}
                      {esSaldoFima && (
                        <p className="text-[11px] text-[#5f6368] mt-0.5">
                          El saldo y las variaciones se leen de la captura al cargarla. Se toma la fecha y hora actual.
                        </p>
                      )}
                      {esChequesBanco && (
                        <p className="text-[11px] text-[#5f6368] mt-0.5">
                          Al subir, el backend lee el encabezado y decide si es el listado de
                          eCheqs o el de cheques físicos en custodia. Hacen falta los dos para
                          que la cartera tenga saldo.
                        </p>
                      )}
                      {esSinClasificar && (
                        <p className="text-[11px] text-[#b06000] mt-0.5">
                          No se pudo leer el documento automáticamente. Elegí abajo la clase; el admin
                          completará los datos a mano.
                        </p>
                      )}
                    </div>
                    <button
                      onClick={() => { elegirArchivo(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                      className="w-8 h-8 rounded-full flex items-center justify-center text-[#5f6368] hover:bg-white/70 transition-colors"
                      title="Quitar"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={onDrop}
                    className={`w-full flex flex-col items-center justify-center gap-2 px-4 py-8 rounded-lg border-2 border-dashed transition-colors ${
                      dragOver ? 'border-[#1a73e8] bg-[#e8f0fe]' : 'border-[#dadce0] bg-[#f8f9fa] hover:bg-[#f1f3f4]'
                    }`}
                  >
                    <Upload className="w-7 h-7 text-[#1a73e8]" />
                    <span className="text-[14px] text-[#202124] font-medium">Arrastrá un archivo o hacé clic para elegir</span>
                    <span className="text-[12px] text-[#80868b]">
                      {[
                        'PDF',
                        'el Excel (.xlsx) del extracto bancario',
                        canLoadFima && 'la captura (JPG/PNG) de Saldo FIMA',
                        canLoadCheques && 'los listados de cheques del banco (.xlsx o .csv)',
                      ].filter(Boolean).join(', ')}
                    </span>
                  </button>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={['application/pdf,.pdf,.xlsx',
                           canLoadFima && '.png,.jpg,.jpeg',
                           canLoadCheques && '.csv'].filter(Boolean).join(',')}
                  onChange={onPickFile}
                  className="hidden"
                />
              </div>

              {/* Documento ilegible: el loader elige la clase a mano. */}
              {esSinClasificar && (
                <div>
                  <label className="flex items-center gap-2 text-[13px] font-medium text-[#5f6368] mb-2">
                    <FileText className="w-4 h-4 text-[#b06000]" /> Clase de documento
                  </label>
                  <select
                    value={tipoManual}
                    onChange={(e) => { setTipoManual(e.target.value); setTipoRetencion(''); }}
                    className={`${inputCls} appearance-none bg-white`}
                  >
                    <option value="">— Elegí la clase —</option>
                    <option value="retencion">Retención</option>
                    <option value="orden_pago">Orden de pago</option>
                    {/* "Cheques recibidos" y "Movimientos de CC" no se ofrecen:
                        siempre tienen el mismo formato y el backend los reconoce
                        automáticamente. */}
                  </select>
                  <p className="text-[12px] text-[#80868b] mt-1">
                    Al subir, el sistema vuelve a leer el documento (OCR). Si lo
                    reconoce, usa esa clase en lugar de la elegida acá.
                  </p>
                </div>
              )}

              {/* Tipo de retención: visible cuando la clase efectiva es retención
                  (detectada o elegida a mano en un documento ilegible). */}
              {esRetencion && (
                <div>
                  <label className="flex items-center gap-2 text-[13px] font-medium text-[#5f6368] mb-2">
                    <Percent className="w-4 h-4 text-[#1a73e8]" /> Tipo de retención
                  </label>
                  <select
                    value={tipoRetencion}
                    onChange={(e) => setTipoRetencion(e.target.value)}
                    className={`${inputCls} appearance-none bg-white`}
                  >
                    <option value="">— Seleccioná un tipo —</option>
                    {tiposRetencion.map((t) => (
                      <option key={t.identificador} value={t.identificador}>
                        {t.nombre}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Comentario */}
              <div>
                <label className="flex items-center gap-2 text-[13px] font-medium text-[#5f6368] mb-2">
                  <MessageSquare className="w-4 h-4 text-[#1a73e8]" /> Comentario
                </label>
                <textarea
                  value={comentario}
                  onChange={(e) => setComentario(e.target.value)}
                  placeholder="Comentario opcional…"
                  rows={4}
                  className={`${inputCls} resize-none`}
                />
              </div>

              {/* Botón */}
              <div className="flex justify-end pt-1">
                <button
                  onClick={subir}
                  disabled={subiendo || verificando}
                  className="inline-flex items-center gap-2 bg-[#1a73e8] hover:bg-[#1765cc] disabled:bg-[#a8c7fa] disabled:cursor-not-allowed text-white px-7 py-3 rounded-full text-[14px] font-medium shadow-[0_1px_2px_rgba(60,64,67,0.3)] transition-colors"
                >
                  {subiendo || verificando ? <Loader2 className="w-5 h-5 animate-spin" /> : <CheckCircle2 className="w-5 h-5" />}
                  {verificando ? 'Verificando…' : subiendo ? 'Cargando…' : 'Cargar documento'}
                </button>
              </div>
            </div>
          </section>

          {/* Columna listado */}
          <section className="bg-white rounded-2xl border border-[#e0e0e0] shadow-[0_1px_3px_rgba(60,64,67,0.15)] flex flex-col overflow-hidden">
            <div className="px-6 py-4 border-b border-[#e0e0e0] flex items-center justify-between">
              <h2 className="text-[16px] text-[#202124] font-medium">
                {isAdmin ? 'Últimas cargas del equipo' : 'Mis últimas cargas'}
              </h2>
              <div className="flex items-center gap-3">
                {/* Reintento masivo: solo admin y solo si hay algo sin copiar */}
                {isAdmin && pendientesDrive > 0 && (
                  <button
                    type="button"
                    onClick={reintentarTodosDrive}
                    disabled={reintentandoTodos}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[12px] font-medium text-[#b06000] bg-[#feefc3] hover:bg-[#fde293] disabled:opacity-60 transition-colors"
                    title="Reintentar la copia a Drive de los documentos pendientes"
                  >
                    {reintentandoTodos
                      ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      : <RefreshCw className="w-3.5 h-3.5" />}
                    Reintentar Drive ({pendientesDrive})
                  </button>
                )}
                {cargandoLista
                  ? <Loader2 className="w-4 h-4 animate-spin text-[#1a73e8]" />
                  : <span className="text-[12px] text-[#5f6368]">{cargas.length} documento{cargas.length === 1 ? '' : 's'}</span>}
              </div>
            </div>

            {cargas.length === 0 ? (
              <div className="flex-1 flex flex-col items-center justify-center text-center py-16 px-6 text-[#5f6368]">
                <Inbox className="w-12 h-12 text-[#dadce0] mb-3" />
                <p className="text-[15px] text-[#202124]">
                  {isAdmin ? 'Todavía no hay documentos cargados' : 'Todavía no cargaste documentos'}
                </p>
                <p className="text-[13px]">
                  {isAdmin ? 'Los que suba el equipo van a aparecer acá.' : 'Los que subas van a aparecer acá.'}
                </p>
              </div>
            ) : (
              <ul className="flex-1 overflow-y-auto divide-y divide-[#f1f3f4]">
                {cargas.map((c) => (
                  <li
                    key={c.id}
                    id={`carga-${c.id}`}
                    className={`flex items-center gap-4 px-6 py-3.5 transition-colors ${
                      destacadoId === c.id
                        ? 'bg-[#fef7e0] ring-2 ring-inset ring-[#f9ab00]'
                        : 'hover:bg-[#f8f9fa]'
                    }`}
                  >
                    <div className="w-10 h-10 rounded-full bg-[#fce8e6] flex items-center justify-center shrink-0">
                      <FileText className="w-5 h-5 text-[#d93025]" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-[14px] text-[#202124] font-medium truncate">{c.cliente_nombre}</p>
                      <p className="text-[12px] text-[#5f6368] truncate">
                        {c.archivo_nombre} · {fmtFecha(c.created_at)}
                      </p>
                      <span className="inline-flex flex-wrap items-center gap-1.5 mt-1">
                        {/* El admin ve las cargas de todos: hay que poder
                            identificar quién subió cada documento. */}
                        {isAdmin && c.user_name && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium text-[#5f6368] bg-[#f1f3f4]">
                            <UserRound className="w-3 h-3" /> {c.user_name}
                          </span>
                        )}
                        {c.tipo_documento === 'saldo_fima' && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium text-[#8430ce] bg-[#f3e8fd]">
                            Saldo FIMA
                            {c.fima_saldo?.saldo != null &&
                              ` · $${Number(c.fima_saldo.saldo).toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                          </span>
                        )}
                        {c.tipo_documento === 'orden_pago' && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium text-[#188038] bg-[#e6f4ea]">
                            <FileText className="w-3 h-3" /> Orden de pago
                          </span>
                        )}
                        {c.tipo_retencion_nombre && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium text-[#1a73e8] bg-[#e8f0fe]">
                            <Percent className="w-3 h-3" /> {c.tipo_retencion_nombre}
                          </span>
                        )}
                      </span>
                    </div>
                    <DriveBadge status={c.drive_status} />
                    {/* Reintento individual: solo si la copia no está en Drive */}
                    {c.drive_status !== 'ok' && (
                      <button
                        type="button"
                        onClick={() => reintentarDrive(c)}
                        disabled={reintentandoId === c.id || reintentandoTodos}
                        className="w-9 h-9 rounded-full flex items-center justify-center text-[#b06000] hover:bg-[#feefc3] disabled:opacity-50 transition-colors shrink-0"
                        title="Reintentar copia a Drive"
                      >
                        {reintentandoId === c.id
                          ? <Loader2 className="w-4 h-4 animate-spin" />
                          : <RefreshCw className="w-4 h-4" />}
                      </button>
                    )}
                    <button
                      type="button"
                      onClick={() => abrirVisor(c)}
                      className="w-9 h-9 rounded-full flex items-center justify-center text-[#1a73e8] hover:bg-[#e8f0fe] transition-colors shrink-0"
                      title="Ver PDF"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      </main>

      {/* Alerta no bloqueante: el tipo elegido no coincide con el detectado */}
      {alerta.open && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4"
          onClick={cerrarAlerta}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-3 px-6 pt-6">
              <div className="w-10 h-10 rounded-full bg-[#feefc3] flex items-center justify-center shrink-0">
                <AlertTriangle className="w-5 h-5 text-[#b06000]" />
              </div>
              <div className="min-w-0">
                <h3 className="text-[17px] text-[#202124] font-medium">¿El tipo de retención es correcto?</h3>
                <p className="text-[13px] text-[#5f6368] mt-0.5">Revisá antes de continuar. No es obligatorio cambiarlo.</p>
              </div>
            </div>

            <div className="px-6 py-4">
              <p className="text-[14px] text-[#202124] leading-relaxed">
                Seleccionaste{' '}
                <span className="font-medium text-[#b06000]">{alerta.seleccionadoNombre}</span>, pero el
                comprobante parece ser{' '}
                <span className="font-medium text-[#188038]">{alerta.detectadoNombre}</span>.
              </p>
              {alerta.evidencia && (
                <div className="mt-3 px-3 py-2 rounded-lg bg-[#f1f3f4] border border-[#e0e0e0]">
                  <p className="text-[11px] text-[#80868b] mb-0.5">Texto detectado en el PDF:</p>
                  <p className="text-[13px] text-[#202124] font-mono break-words">“{alerta.evidencia}”</p>
                </div>
              )}
            </div>

            <div className="flex flex-col sm:flex-row-reverse gap-2 px-6 pb-6">
              <button
                type="button"
                onClick={confirmarCambio}
                className="inline-flex items-center justify-center gap-2 bg-[#1a73e8] hover:bg-[#1765cc] text-white px-5 py-2.5 rounded-full text-[14px] font-medium transition-colors"
              >
                <CheckCircle2 className="w-4 h-4" /> Cambiar a {alerta.detectadoNombre}
              </button>
              <button
                type="button"
                onClick={mantenerYSubir}
                className="inline-flex items-center justify-center px-5 py-2.5 rounded-full text-[14px] font-medium text-[#5f6368] hover:bg-[#f1f3f4] border border-[#dadce0] transition-colors"
              >
                Mantener {alerta.seleccionadoNombre} y subir igual
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Visor de PDF en modal */}
      {visor.open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          onClick={cerrarVisor}
        >
          <div
            className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl h-[90vh] flex flex-col overflow-hidden"
            style={dragVisor.panelStyle}
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="flex items-center justify-between px-5 py-3 border-b border-[#e0e0e0]"
              {...dragVisor.handleProps}
            >
              <div className="flex items-center gap-2 min-w-0">
                <FileText className="w-5 h-5 text-[#d93025] shrink-0" />
                <h3 className="text-[15px] text-[#202124] font-medium truncate">{visor.nombre}</h3>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {visor.url && (
                  <a
                    href={visor.url}
                    download={visor.nombre}
                    className="w-9 h-9 rounded-full flex items-center justify-center text-[#5f6368] hover:bg-[#f1f3f4] transition-colors"
                    title="Descargar"
                  >
                    <Download className="w-4 h-4" />
                  </a>
                )}
                <button
                  type="button"
                  onClick={cerrarVisor}
                  className="w-9 h-9 rounded-full flex items-center justify-center text-[#5f6368] hover:bg-[#f1f3f4] transition-colors"
                  title="Cerrar"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            <div className="flex-1 bg-[#f1f3f4] min-h-0">
              {visor.cargando || !visor.url ? (
                <div className="h-full flex flex-col items-center justify-center text-[#5f6368]">
                  <Loader2 className="w-8 h-8 animate-spin text-[#1a73e8] mb-2" />
                  <p className="text-[13px]">Cargando documento…</p>
                </div>
              ) : (
                <iframe
                  src={visor.url}
                  title={visor.nombre}
                  className={`w-full h-full border-0 ${dragVisor.dragging ? 'pointer-events-none' : ''}`}
                />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LoadDocsPage;
